import { onScopeDispose, readonly, ref } from 'vue'

/**
 * État de la connexion, nu — `navigator.onLine` suivi des événements
 * `online`/`offline`.
 *
 * Utile parce que l'API seule ne dit rien en pratique : une requête qui part
 * hors ligne échoue avec l'erreur générique « serveur injoignable »,
 * indiscernable d'un backend arrêté (`ApiError.isNetwork`, statut 0). Le
 * navigateur, lui, SAIT qu'il est hors ligne — afficher cette distinction est
 * la différence entre « reconnectez-vous plus tard » et « le logiciel est
 * cassé ».
 *
 * Note de fiabilité, assumée : un portable connecté à un réseau sans accès
 * internet reste « en ligne » pour `navigator.onLine`. La bannière couvre la
 * coupure franche (avion, wifi perdu) — pas le réseau mensonger, que seule une
 * requête détecte, déjà signalée par le badge du canal temps réel.
 */
export function useOnlineStatus() {
  // jsdom expose `navigator.onLine = true` ; hors navigateur, on suppose
  // « en ligne » pour ne pas afficher une bannière en prérendu.
  const enLigne = ref(typeof navigator === 'undefined' ? true : navigator.onLine)

  const mettreAJour = (): void => {
    enLigne.value = navigator.onLine
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', mettreAJour)
    window.addEventListener('offline', mettreAJour)
    onScopeDispose(() => {
      window.removeEventListener('online', mettreAJour)
      window.removeEventListener('offline', mettreAJour)
    })
  }

  return readonly(enLigne)
}
